'use client'

import { useTranslations } from 'next-intl'

import type { NavGroup } from '@graftcode/design-system'
import { useShallow } from 'zustand/react/shallow'

import { LAYOUT_TEST_IDS } from '@common/common-test-ids.const'
import { AppVariant } from '@common/enums'

import { selectProjectsShellState } from '@modules/projects/stores/projects.selector'
import { useProjectsStore } from '@modules/projects/stores/projects.store'
import { selectWorkspacesShellState } from '@modules/workspaces/stores/workspaces.selector'
import { useWorkspacesStore } from '@modules/workspaces/stores/workspaces.store'
import { selectGlobalLoading } from '@stores/layout/layout.selector'
import { useLayoutStore } from '@stores/layout/layout.store'

import { Sidebar } from '../Sidebar'

import { useLayoutNavigation } from './hooks/useLayoutNavigation'
import { SidebarAddNew } from './SidebarAddNew'
import { SidebarSettingsBack } from './SidebarSettingsBack'

type LayoutSidebarProps = {
  isSettingsRoute: boolean
  isVisionRoute: boolean
  selectedNavId: AppVariant
  sidebarMenuItems: NavGroup[]
}

export function LayoutSidebar({ selectedNavId, sidebarMenuItems, isSettingsRoute, isVisionRoute }: LayoutSidebarProps) {
  const t = useTranslations('core')
  const layoutLoading = useLayoutStore(selectGlobalLoading)
  const sidebarCollapsed = useLayoutStore((state) => state.sidebarCollapsed)
  const openInviteProjectDialog = useLayoutStore((state) => state.openInviteProjectDialog)
  const { currentWorkspace, loading: workspacesLoading } = useWorkspacesStore(useShallow(selectWorkspacesShellState))
  const { loading: projectsLoading } = useProjectsStore(useShallow(selectProjectsShellState))
  const { handleNavigationClick, handleWorkspaceSelect, handleNewActionClick } = useLayoutNavigation({
    isPortal: selectedNavId === AppVariant.PORTAL,
  })

  const workspaceSelectorBusy = layoutLoading || workspacesLoading || projectsLoading || !currentWorkspace

  const navbarTopSlot = isSettingsRoute ? (
    <SidebarSettingsBack onNavigationClick={handleNavigationClick} />
  ) : selectedNavId === AppVariant.PORTAL ? (
    <SidebarAddNew onItemClick={handleNewActionClick} />
  ) : null

  return (
    <div data-testid={LAYOUT_TEST_IDS.layoutSidebarHost} style={{ display: 'contents' }}>
      <aside className="sidebar" data-testid={LAYOUT_TEST_IDS.sidebar}>
        <Sidebar
          selectedNavId={selectedNavId}
          menuGroups={sidebarMenuItems}
          hideNavItemIcons={selectedNavId === AppVariant.VISION && isVisionRoute}
          navbarTopSlot={navbarTopSlot}
          showInviteButton={!isSettingsRoute}
          inviteButtonText={sidebarCollapsed ? '' : t('sidebar.inviteToProject')}
          workspaceSelectorLoading={workspaceSelectorBusy}
          workspaceSelectorDisabled={workspaceSelectorBusy}
          onInviteClick={openInviteProjectDialog}
          onWorkspaceSelect={handleWorkspaceSelect}
          onNavigationClick={handleNavigationClick}
        />
      </aside>
    </div>
  )
}
